import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AdminAuction } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useEndAuction } from '../hooks/use-auction-mutations';

/** Ends a live auction now, settled like the timed end: a sale places the winner's order. */
export function EndAuctionDialog({
  storeId,
  auction,
  open,
  onOpenChange,
}: {
  storeId: string;
  auction: AdminAuction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['auctions', 'common']);
  const format = useFormat();
  const end = useEndAuction(storeId);
  const message = useErrorMessage();
  const sells = auction.leader !== null && auction.reserveMet && auction.currentPrice !== null;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) end.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('auctions:end.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {sells && auction.leader && auction.currentPrice
              ? t('auctions:end.sells', {
                  bidder: auction.leader.name ?? auction.leader.email,
                  price: format.money(auction.currentPrice),
                })
              : auction.leader
                ? t('auctions:end.reserveNotMet')
                : t('auctions:end.noBids')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {end.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(end.error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={end.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={end.isPending}
            onClick={(event) => {
              event.preventDefault();
              end.mutate(auction.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {end.isPending ? t('auctions:actions.working') : t('auctions:end.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
